import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import { FaReact, FaJs, FaNode, FaHtml5, FaCss3Alt, FaPython, FaGitAlt, FaBootstrap, FaVuejs } from 'react-icons/fa';
import { SiMongodb, SiExpress, SiNextdotjs, SiTailwindcss, SiFigma, SiMysql, SiNestjs, SiJira, SiPostman, SiRender, SiVercel, SiRedux } from 'react-icons/si';
import './Skills.css';

const Skills = () => {
  const [activeCategory, setActiveCategory] = useState('frontend');
  // eslint-disable-next-line no-unused-vars
  const [progressBars, setProgressBars] = useState({});
  const ref = useRef(null);

  const skillCategories = useMemo(() => ({
    frontend: {
      title: 'Frontend Development',
      skills: [
        { name: 'React', level: 90, icon: <FaReact />, color: '#61DAFB' },
        { name: 'JavaScript', level: 85, icon: <FaJs />, color: '#F7DF1E' },
        { name: 'HTML5', level: 95, icon: <FaHtml5 />, color: '#E34F26' },
        { name: 'CSS3', level: 90, icon: <FaCss3Alt />, color: '#1572B6' },
        { name: 'Tailwind CSS', level: 85, icon: <SiTailwindcss />, color: '#06B6D4' },
        { name: 'Next.js', level: 75, icon: <SiNextdotjs />, color: '#000000' },
        { name: 'Vue.js', level: 70, icon: <FaVuejs />, color: '#4FC08D' },
        { name: 'Bootstrap', level: 80, icon: <FaBootstrap />, color: '#7952B3' },
        { name: 'Redux', level: 75, icon: <SiRedux />, color: '#764ABC' }
      ]
    },
    backend: {
      title: 'Backend Development',
      skills: [
        { name: 'Node.js', level: 85, icon: <FaNode />, color: '#339933' },
        { name: 'Express.js', level: 80, icon: <SiExpress />, color: '#000000' },
        { name: 'Nest.js', level: 75, icon: <SiNestjs />, color: '#E0234E' },
        { name: 'Python', level: 75, icon: <FaPython />, color: '#3776AB' },
        { name: 'MongoDB', level: 70, icon: <SiMongodb />, color: '#47A248' },
        { name: 'MySQL', level: 70, icon: <SiMysql />, color: '#4479A1' }
      ]
    },
    tools: {
      title: 'Tools & Technologies',
      skills: [
        { name: 'Git', level: 85, icon: <FaGitAlt />, color: '#F05032' },
        { name: 'Figma', level: 80, icon: <SiFigma />, color: '#F24E1E' },
        { name: 'Jira', level: 75, icon: <SiJira />, color: '#0052CC' },
        { name: 'Postman', level: 80, icon: <SiPostman />, color: '#FF6C37' },
        { name: 'Render', level: 75, icon: <SiRender />, color: '#46E3B7' },
        { name: 'Vercel', level: 80, icon: <SiVercel />, color: '#000000' }
      ]
    }
  }), []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const skillVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    },
    hover: {
      scale: 1.05,
      y: -5,
      transition: {
        duration: 0.3
      }
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      skillCategories[activeCategory].skills.forEach((skill, index) => {
        setTimeout(() => {
          setProgressBars(prev => ({
            ...prev,
            [skill.name]: skill.level
          }));
        }, index * 200);
      });
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory]);


  const handleCategoryChange = (category) => {
    setActiveCategory(category);
    setProgressBars({});
  };

  return (
    <section id="skills" className="skills">
      <div className="container">
        <motion.div
          className="skills-header"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.h2 className="section-title" variants={itemVariants}>
            My Skills
          </motion.h2>
          <motion.p className="section-subtitle" variants={itemVariants}>
            Technologies and tools I work with
          </motion.p>
        </motion.div>

        <motion.div
          className="skills-categories"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {Object.keys(skillCategories).map((category) => (
            <motion.button
              key={category}
              className={`category-btn ${activeCategory === category ? 'active' : ''}`}
              onClick={() => handleCategoryChange(category)}
              variants={itemVariants}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {skillCategories[category].title}
            </motion.button>
          ))}
        </motion.div>

        <motion.div
          className="skills-grid"
          key={activeCategory}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          ref={ref}
        >
          {skillCategories[activeCategory].skills.map((skill, index) => (
            <motion.div
              key={skill.name}
              className="skill-card"
              variants={skillVariants}
              initial="hidden"
              animate="visible"
              whileHover="hover"
              transition={{ delay: index * 0.1 }}
            >
              <div className="skill-header">
                <div 
                  className="skill-icon"
                  style={{ color: skill.color }}
                >
                  {skill.icon}
                </div>
                <div className="skill-info">
                  <h4>{skill.name}</h4>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          className="skills-summary"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.div className="summary-card" variants={itemVariants}>
            <h3>Why These Technologies?</h3>
            <p>
              I choose technologies that provide the best balance of performance, 
              developer experience, and maintainability. My focus is on modern, 
              industry-standard tools that enable rapid development without 
              compromising on quality.
            </p>
          </motion.div>
          
          <motion.div className="summary-card" variants={itemVariants}>
            <h3>Continuous Learning</h3>
            <p>
              Technology evolves rapidly, and so do I. I'm constantly learning 
              new frameworks, tools, and best practices to stay at the forefront 
              of web development and deliver cutting-edge solutions.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Skills;
